import React, { useState } from 'react';
import { Card } from 'primereact/card';
import { Button } from 'primereact/button';
import { Message } from 'primereact/message';
import { toast } from 'react-toastify';
import axiosInstance from '../../../config/axios.config';
import { SmartHeader, SmartField, NoRecordsCard } from './_SmartFieldRenderer';

const Section = ({ icon, title, children }) => (
    <div className="pt-3 pb-3 border-bottom-1 surface-border">
        <div className="flex align-items-center gap-2 mb-3">
            <i className={`pi ${icon} text-primary`} aria-hidden="true" />
            <span className="text-sm font-semibold text-900 uppercase">{title}</span>
        </div>
        <div className="grid">{children}</div>
    </div>
);

const formatGender = (g) => (g === 'M' ? 'Male' : g === 'F' ? 'Female' : g || 'N/A');

const formatRelativeName = (vd) => {
    if (vd.relativeType && vd.relativeName) {
        const t = vd.relativeType.toLowerCase();
        if (t === 'father') return `Father: ${vd.relativeName}`;
        if (t === 'husband') return `Husband: ${vd.relativeName}`;
        return `${vd.relativeType}: ${vd.relativeName}`;
    }
    if (vd.fatherName) return `Father: ${vd.fatherName}`;
    if (vd.husbandName) return `Husband: ${vd.husbandName}`;
    return null;
};

const VoterIdOutputComponent = ({ reportData, serviceDetails, pdfDownloadUrl, onDownloadPDF, isSharedView }) => {
    const [downloadingPDF, setDownloadingPDF] = useState(false);

    if (reportData?.code === '1004') {
        return <NoRecordsCard subtitle="There is no data found for the provided details. Please enter valid details." />;
    }

    let voterDetails = {};
    let parsedData = reportData;
    const pdfPasswordHint =
        reportData?.pdf_password_hint ||
        reportData?.data?.pdf_password_hint ||
        parsedData?.pdf_password_hint ||
        parsedData?.data?.pdf_password_hint ||
        null;

    try {
        if (reportData?.report_json) {
            const parsedJson =
                typeof reportData.report_json === 'string' ? JSON.parse(reportData.report_json) : reportData.report_json;
            parsedData = parsedJson;
            voterDetails = parsedJson?.response || parsedJson || {};
        } else {
            voterDetails =
                reportData?.data?.response ||
                reportData?.response ||
                reportData?.data?.voter_details ||
                reportData?.voter_details ||
                reportData?.data ||
                {};
        }
    } catch {
        voterDetails =
            reportData?.data?.response ||
            reportData?.response ||
            reportData?.data?.voter_details ||
            reportData?.voter_details ||
            reportData?.data ||
            {};
    }

    const reportId = reportData?.report_id || reportData?.data?.report_id;

    const pdfUrl =
        pdfDownloadUrl ||
        parsedData?.pdf_download_url ||
        parsedData?.pdf_url ||
        reportData?.pdf_url ||
        reportData?.pdf_download_url ||
        reportData?.data?.pdf_url ||
        reportData?.data?.pdf_download_url;

    const remainingBalance = reportData?.remaining_credits || serviceDetails?.available_credits;

    const handleDownloadPDF = async () => {
        if (pdfPasswordHint) {
            toast.info(pdfPasswordHint, { position: 'top-center', autoClose: 15000 });
        }

        if (onDownloadPDF && typeof onDownloadPDF === 'function') {
            const result = onDownloadPDF();
            if (result) return;
        }

        if (!pdfUrl && !reportId) {
            toast.error('PDF download URL not available');
            return;
        }

        setDownloadingPDF(true);
        try {
            toast.info('Preparing PDF download...');
            let downloadUrl = pdfUrl;

            if (!downloadUrl && reportId) {
                const response = await axiosInstance.post(`/api/tartan/voter-id/generate-pdf/${reportId}`);
                if (response.data.success) {
                    downloadUrl = response.data.pdf_url || response.data.download_url;
                }
            }

            if (downloadUrl) {
                if (downloadUrl.startsWith('http')) {
                    window.open(downloadUrl, '_blank', 'noopener,noreferrer');
                    toast.success('PDF download started');
                } else {
                    const response = await axiosInstance.get(downloadUrl, {
                        responseType: 'blob',
                        headers: { Accept: 'application/pdf' },
                    });
                    const blob = new Blob([response.data], { type: 'application/pdf' });
                    const url = window.URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    link.href = url;
                    link.download = `voter_id_verification_${reportId || Date.now()}.pdf`;
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    window.URL.revokeObjectURL(url);
                    toast.success('PDF downloaded successfully');
                }
            } else {
                toast.error('Unable to generate PDF download link');
            }
        } catch (err) {
            toast.error('Failed to download PDF. Please try again.');
        } finally {
            setDownloadingPDF(false);
        }
    };

    const relativeLabel = formatRelativeName(voterDetails);

    return (
        <div>
            <SmartHeader
                title="Voter ID Verification Report"
                icon="pi-id-card"
                remainingBalance={remainingBalance}
                shouldHideCredits={isSharedView}
            />

            <Card>
                <Section icon="pi-user" title="Personal Details">
                    <SmartField name="full_name" value={voterDetails.name} />
                    <SmartField name="voter_number" value={voterDetails.epicNo || voterDetails.voterNo} highlight />
                    <SmartField name="gender" value={formatGender(voterDetails.gender)} />
                    {voterDetails.age && <SmartField name="age" value={`${voterDetails.age} years`} />}
                    {relativeLabel && <SmartField name="relative" value={relativeLabel} />}
                </Section>

                <Section icon="pi-map-marker" title="Location Information">
                    <SmartField name="state" value={voterDetails.state} />
                    <SmartField name="district" value={voterDetails.district} />
                    <SmartField name="parliamentary_constituency" value={voterDetails.pcName} />
                    <SmartField name="polling_station" value={voterDetails.pollingStation} />
                </Section>

                <div className="text-center pt-3">
                    <Button
                        label="Download Voter ID Report"
                        icon="pi pi-download"
                        onClick={handleDownloadPDF}
                        loading={downloadingPDF}
                        disabled={!pdfUrl && !reportId}
                        size="small"
                    />
                    {pdfPasswordHint && (
                        <div className="mt-2">
                            <Message severity="info" text={pdfPasswordHint} />
                        </div>
                    )}
                </div>
            </Card>
        </div>
    );
};

export default VoterIdOutputComponent;
